import React from "react";
import { useTranslation } from "react-i18next";
import { Play } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { StarRating } from "@/components/ui/star-rating";
import { formatLogText } from "../../../utils/formatLogText";
import { getParticipantColor, isColorLight } from "../../../utils/participantColors";
import { formatTimecode } from "../../../hooks/useTimecode";

export interface LogCardProps {
  log: any; // using any to accommodate different structures
  participants: any[];
  directors?: any[];
  accentColor: string;
  getCameraName: (id: string) => string;
  getTagColor: (id: string) => string;
  getTagLabel: (id: string) => string;

  // Transcripts
  transcripts?: any[];
  expandedTranscriptLogIds?: Set<string>;
  toggleTranscriptExpand?: (id: string) => void;

  // Selection
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;

  // Highlights
  highlightText?: string | null;
  isHighlighted?: boolean;

  // Context
  storyId?: string;

  // Actions
  onDelete?: (id?: string) => void;
  onEdit?: (log: any) => void;
  // Inline text editing: when provided, a pencil action lets the user edit the
  // card's text right in place; on save it fires with the new text so the caller
  // can persist it (e.g. update the Plate node in the Cortes editor).
  onSaveText?: (newText: string) => void;
  // CortesEditor owns its text through Plate. When this is provided, render the
  // Slate leaves directly inside the card instead of mounting LogCard's
  // textarea + Guardar/Cancelar editor.
  blockEditorText?: React.ReactNode;

  // Drag and Drop
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;

  // Assembly specifics
  isAssembly?: boolean;
  isDragOver?: boolean;
  isDragging?: boolean;
  index?: number;
  showDragHandle?: boolean;
  onNavigateSource?: (sourceId: string, itemId: string, itemType: string) => void;
  onUpdateItem?: (id: string, updates: any) => void;

  // Relay clip (StoryProducer): fuerza el render como OTF y hace el timecode
  // clickeable para reproducir el video grabado del relay en ese instante.
  displayAsOtf?: boolean;
  onTimecodeClick?: (log: any) => void;
  // Used by Cortes/VTR cards: a click anywhere on the source card loads its
  // relay clip, while the inner action controls keep stopping propagation.
  onCardClick?: (log: any) => void;
  // Compact layout (used by the History panel): tightens paddings, margins and
  // font sizes to match the Recent Logs list density. The expandable metadata
  // panel is intentionally left untouched.
  compact?: boolean;
  // Renders an editable star rating in the card header action bar, so the
  // ranking can be set without expanding the metadata panel (History sidebar).
  // Requires onUpdateItem to persist the change.
  showInlineRating?: boolean;
}

export const BlockRowContext = React.createContext<{
  handleRef?: any;
  onDeleteBlock?: () => void;
  onNavigateSource?: (sourceId: string, itemId: string, itemType: string) => void;
} | null>(null);

export const LogCard: React.FC<LogCardProps> = ({
  log,
  participants,
  directors,
  accentColor,
  getCameraName,
  getTagColor,
  getTagLabel,
  transcripts,
  expandedTranscriptLogIds,
  toggleTranscriptExpand,
  selectable,
  selected,
  onToggleSelect,
  highlightText,
  isHighlighted,
  onDelete,
  onEdit,
  onSaveText,
  blockEditorText,
  draggable,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  isAssembly,
  isDragOver,
  isDragging,
  index,
  showDragHandle,
  onNavigateSource,
  onUpdateItem,
  storyId,
  displayAsOtf,
  onTimecodeClick,
  onCardClick,
  compact,
  showInlineRating,
}) => {
  const { t } = useTranslation();
  const blockRowCtx = React.useContext(BlockRowContext);
  const hasDelete = !!onDelete || !!blockRowCtx?.onDeleteBlock;
  const displayDrag = (isAssembly && showDragHandle) || !!blockRowCtx?.handleRef;
  const finalNavigate = onNavigateSource || blockRowCtx?.onNavigateSource;
  const isTranscriptExpanded = expandedTranscriptLogIds?.has(log.id) || false;
  const transcriptCount = transcripts?.length || 0;
  const [isMetadataExpanded, setIsMetadataExpanded] = React.useState(false);
  const [isWordsExpanded, setIsWordsExpanded] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const [isEditingText, setIsEditingText] = React.useState(false);
  const [draftText, setDraftText] = React.useState("");
  const rootRef = React.useRef<HTMLDivElement>(null);

  // Only one card should be in edit mode at a time, and stepping off a card
  // returns it to its normal state. A capture-phase mousedown handler exits
  // inline editing whenever the pointer lands outside this card — clicking
  // another card is "outside" this one, so opening a second editor closes the
  // first as a side effect. Capture phase ensures inner stopPropagation on
  // sibling cards doesn't suppress it.
  React.useEffect(() => {
    if (!isEditingText) return;
    const handlePointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setIsEditingText(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown, true);
    return () => document.removeEventListener("mousedown", handlePointerDown, true);
  }, [isEditingText]);

  // Normalize endAt: logs use 'endAt', assembly items use 'tcOut'.
  // For transcriptions/OTFs, derive from the last transcript segment's endAt.
  const normalizedEndAt = React.useMemo(() => {
    if (log.endAt || log.tcOut) return log.endAt || log.tcOut;
    // Derive from last transcript segment
    if (transcripts && transcripts.length > 0) {
      const lastSeg = transcripts[transcripts.length - 1];
      if (lastSeg?.endAt || lastSeg?.end_at) return lastSeg.endAt || lastSeg.end_at;
    }
    return null;
  }, [log.endAt, log.tcOut, transcripts]);

  // Smart duration: compute from tcIn/tcOut if available, or from transcript segments.
  const computedDuration = React.useMemo(() => {
    const tcIn = log.tcIn || log.startAt;
    const tcOut = log.tcOut || log.endAt || normalizedEndAt;
    if (tcIn && tcOut) {
      const inMs = new Date(tcIn).getTime();
      const outMs = new Date(tcOut).getTime();
      if (!isNaN(inMs) && !isNaN(outMs) && outMs > inMs) {
        const diffMs = outMs - inMs;
        const h = Math.floor(diffMs / 3600000);
        const m = Math.floor((diffMs % 3600000) / 60000);
        const s = Math.floor((diffMs % 60000) / 1000);
        return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
      }
    }
    const dur = log.duration;
    if (dur && dur !== "00:00:00" && dur !== log.tc) return dur;
    return null;
  }, [log.tcIn, log.startAt, log.tcOut, log.endAt, normalizedEndAt, log.duration, log.tc]);

  // Derived OUT timecode: avoids timezone issues by calculating offset from log.tc (IN)
  const { derivedOutTc, derivedDuration } = React.useMemo(() => {
    let durationMs = 0;
    if (log.words && log.words.length > 0) {
      const firstWord = log.words[0];
      const lastWord = log.words[log.words.length - 1];
      if (typeof lastWord.end === "number" && typeof firstWord.start === "number") {
        durationMs = (lastWord.end - firstWord.start) * 1000;
      } else if (typeof lastWord.end === "number") {
        durationMs = lastWord.end * 1000;
      }
    } else if (computedDuration && computedDuration.includes(":")) {
      const parts = computedDuration.split(":");
      if (parts.length === 3) {
        durationMs =
          (parseInt(parts[0], 10) * 3600 + parseInt(parts[1], 10) * 60 + parseInt(parts[2], 10)) *
          1000;
      }
    }

    let outTc = null;
    if (log.tc && log.tc.includes(":") && durationMs > 0) {
      const tcParts = log.tc.split(/[:;]/);
      if (tcParts.length >= 3) {
        let h = parseInt(tcParts[0], 10);
        let m = parseInt(tcParts[1], 10);
        let s = parseInt(tcParts[2], 10);
        const inFrames = tcParts.length >= 4 ? parseInt(tcParts[3], 10) : 0;
        const inFrameMs = Math.round((inFrames / 29.97) * 1000);

        let totalMs = (h * 3600 + m * 60 + s) * 1000 + inFrameMs + durationMs;

        const newS = Math.floor(totalMs / 1000) % 60;
        const newM = Math.floor(totalMs / 60000) % 60;
        const newH = Math.floor(totalMs / 3600000) % 24;
        const frames = Math.floor(((totalMs % 1000) / 1000) * 29.97);

        const pad = (n: number) => n.toString().padStart(2, "0");
        outTc = `${pad(newH)}:${pad(newM)}:${pad(newS)};${pad(frames)}`;
      }
    }

    let durTc = null;
    if (durationMs > 0) {
      const dh = Math.floor(durationMs / 3600000);
      const dm = Math.floor((durationMs % 3600000) / 60000);
      const ds = Math.floor((durationMs % 60000) / 1000);
      const pad = (n: number) => n.toString().padStart(2, "0");
      durTc = `${pad(dh)}:${pad(dm)}:${pad(ds)}`;
    } else {
      durTc = computedDuration;
    }

    return { derivedOutTc: outTc, derivedDuration: durTc };
  }, [log.tc, log.words, computedDuration]);

  // Function to highlight matching text
  const _renderTextWithHighlight = (text: string, highlight: string | null | undefined) => {
    if (!highlight || !text) return text;
    const escapedHighlight = highlight.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const parts = text.split(new RegExp(`(${escapedHighlight})`, "gi"));
    return parts.map((part, i) => {
      if (part.toLowerCase() === highlight.toLowerCase()) {
        return (
          <mark
            key={i}
            style={{
              background: "rgba(255, 220, 0, 0.9)",
              color: "#000",
              padding: "2px 4px",
              borderRadius: "3px",
              fontWeight: 600,
            }}
          >
            {part}
          </mark>
        );
      }
      return part;
    });
  };

  const isTranscription =
    (log.type === "transcription" || log.type === "transcript") && !displayAsOtf;
  const isOtf = log.type === "otf" || !!displayAsOtf;
  const isPleca = log.type === "pleca";
  const isRecurso = log.type === "recurso";

  let bgColor =
    highlightText || isHighlighted ? "rgba(255, 220, 0, 0.15)" : "rgba(30, 30, 30, 0.9)";
  let borderStr =
    highlightText || isHighlighted ? "2px solid #ffdc00" : "1px solid rgba(247, 45, 96, 0.1)";
  let borderLeftStr = highlightText || isHighlighted ? "4px solid #ffdc00" : "3px solid #f72d60";
  let boxShadowStr =
    highlightText || isHighlighted
      ? "0 0 20px rgba(255, 220, 0, 0.3)"
      : "0 1px 8px rgba(247, 45, 96, 0.05)";
  let tcColor = accentColor;
  let textColor = "#ccc";
  let textStyle = "normal";
  let baseBorderColor = "#f72d60";

  if (!highlightText && !isHighlighted) {
    if (isOtf) {
      bgColor = "rgba(0, 229, 255, 0.12)";
      borderStr = "1px solid rgba(0, 229, 255, 0.2)";
      borderLeftStr = "3px solid #00e5ff";
      baseBorderColor = "#00e5ff";
      boxShadowStr = "0 1px 8px rgba(0, 229, 255, 0.1)";
      tcColor = "#00e5ff";
    } else if (isTranscription) {
      bgColor = "rgba(156, 39, 176, 0.12)";
      borderStr = "1px solid rgba(156, 39, 176, 0.2)";
      borderLeftStr = "3px solid #9c27b0";
      baseBorderColor = "#9c27b0";
      boxShadowStr = "0 1px 8px rgba(156, 39, 176, 0.1)";
      tcColor = "#9c27b0";
      textColor = "#aaa";
      textStyle = "italic";
    }

    if (log.rating && log.rating > 0) {
      borderStr = "1px solid rgba(255, 170, 0, 0.4)";
      borderLeftStr = "none";
      bgColor = `linear-gradient(to bottom, var(--color-accent-warning) 50%, ${baseBorderColor} 50%) left/3px 100% no-repeat, ${bgColor}`;
    }
  }

  // Use either text or title from log structure
  const textContent = log.text || log.title;

  // YYYY-MM-DD from the log's recording timestamp. start_at arrives from the
  // DB as a timezone-less ISO string but represents UTC — force UTC parsing
  // so browsers west of UTC don't shift to the next day.
  const dateLabel = (() => {
    let raw = log.start_at || log.startAt || log.created_at || log.createdAt;
    if (raw == null || raw === "") {
      const candidate = log.tcIn;
      if (candidate != null && candidate !== "") {
        if (typeof candidate === "string") {
          if (candidate.includes("-") || candidate.includes("T")) {
            raw = candidate;
          }
        } else {
          raw = candidate;
        }
      }
    }
    if (raw == null || raw === "") return "";
    let input: string | number | Date = raw;
    if (typeof raw === "string") {
      const hasTz = /(Z|[+-]\d{2}:?\d{2})$/.test(raw);
      const isIsoDateTime = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw);
      if (isIsoDateTime && !hasTz) input = `${raw}Z`;
    }
    const d = input instanceof Date ? input : new Date(input);
    if (Number.isNaN(d.getTime())) return "";
    return new Intl.DateTimeFormat("es-ES", { day: "numeric", month: "long" }).format(d);
  })();

  // Clean author if needed (mimic cleanSpeakerName from AssemblyManager without another dependency)
  const cleanAuthorName = (name: string) => {
    if (!name) return "";
    return name.split("@")[0].split(".")[0];
  };

  // Copy the card's textual content (header + text + transcript segments) to the
  // clipboard. Restored after the RESUMEN merge dropped it from the universal
  // LogCard; it renders on every card, so it comes back in library and cortes.
  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Author is intentionally excluded from the copied payload (per request);
    // only date + timecode go in the header.
    const header: string[] = [];
    if (dateLabel) header.push(dateLabel);
    if (log.tc) header.push(log.tc);
    const lines: string[] = [];
    if (header.length) lines.push(header.join(" · "));
    if (textContent) lines.push(textContent);
    if (transcripts && transcripts.length > 0) {
      transcripts.forEach((seg: any) => {
        const start = seg.start != null ? `${Math.floor(seg.start)}s ` : "";
        const sp = seg.speaker ? `${seg.speaker}: ` : "";
        if (seg.text) lines.push(`${start}${sp}${seg.text}`);
      });
    }
    const payload = lines.filter(Boolean).join("\n").toUpperCase();
    if (!payload) return;
    navigator.clipboard
      .writeText(payload)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  };

  return (
    <div
      ref={rootRef}
      data-log-id={log.id}
      // Disable drag while inline-editing so the textarea stays selectable/typeable
      // (a draggable parent otherwise swallows text selection inside the field).
      draggable={draggable && !isEditingText}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={
        onCardClick
          ? (e) => {
              if (!e.defaultPrevented) onCardClick(log);
            }
          : undefined
      }
      style={{
        display: showDragHandle || selectable ? "flex" : "block",
        gap: showDragHandle || selectable ? "10px" : "0",
        alignItems: showDragHandle || selectable ? "flex-start" : "initial",
        background: isDragOver ? "rgba(16, 185, 129, 0.15)" : bgColor,
        borderRadius: compact ? "6px" : "10px",
        padding: compact
          ? log.rating && log.rating > 0 && !highlightText && !isHighlighted
            ? "8px 10px 8px 13px"
            : "8px 10px"
          : log.rating && log.rating > 0 && !highlightText && !isHighlighted
            ? "14px 18px 14px 21px"
            : "14px 18px",
        border: isDragOver ? "1px dashed var(--color-role-sp)" : borderStr,
        borderLeft: isDragOver ? borderLeftStr : borderLeftStr,
        marginBottom: compact ? "4px" : "8px",
        position: "relative",
        animation: highlightText || isHighlighted ? "highlightPulse 2s ease-in-out" : "none",
        boxShadow: compact ? "none" : boxShadowStr,
        opacity: isDragging ? 0.4 : 1,
        cursor: draggable ? "grab" : onCardClick ? "pointer" : "default",
        userSelect: draggable ? "none" : undefined,
        WebkitUserSelect: draggable ? "none" : undefined,
        transition: "all 0.2s ease",
      }}
    >
      {((showDragHandle && !isAssembly) || selectable) && (
        <div
          contentEditable={blockEditorText !== undefined ? false : undefined}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "10px",
            marginTop: "2px",
          }}
        >
          {showDragHandle && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "2px",
              }}
            >
              <i className="bx bx-grid-vertical" style={{ fontSize: "1.1rem", color: "#555" }}></i>
              {index !== undefined && (
                <span
                  style={{ color: "#666", fontSize: "0.75rem", width: "16px", textAlign: "center" }}
                >
                  {index + 1}
                </span>
              )}
            </div>
          )}
          {/* Selection Checkbox for Log */}
          {selectable && (
            <div
              onClick={(e) => {
                e.stopPropagation();
                if (onToggleSelect) onToggleSelect();
              }}
              style={{
                width: "20px",
                height: "20px",
                borderRadius: "4px",
                border: selected ? "none" : "2px solid rgba(255,255,255,0.3)",
                background: selected ? "var(--color-accent-success)" : "rgba(0,0,0,0.3)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              {selected && (
                <i className="bx bx-check" style={{ color: "#fff", fontSize: "1rem" }}></i>
              )}
            </div>
          )}
        </div>
      )}

      <div style={{ flex: 1, minWidth: 0, width: "100%" }}>
        {/* ACTION BUTTONS (Top Right) */}
        <div
          contentEditable={blockEditorText !== undefined ? false : undefined}
          style={{
            position: "absolute",
            top: compact ? "8px" : "12px",
            right: compact ? "8px" : "12px",
            display: "flex",
            gap: compact ? "4px" : "8px",
            alignItems: "center",
            zIndex: 60,
          }}
        >
          {/* Inline Star Rating (editable) */}
          {showInlineRating && onUpdateItem && (
            <div
              onClick={(e) => e.stopPropagation()}
              title={t("storyproducer.ranking", "Ranking")}
              style={{
                display: "flex",
                alignItems: "center",
                marginRight: compact ? "2px" : "4px",
              }}
            >
              <StarRating
                value={log.rating || 0}
                onChange={(v) => onUpdateItem(log.id, { rating: v > 0 ? v : undefined })}
                inactiveColor="rgba(255,255,255,0.25)"
                size={compact ? "0.95rem" : "1.05rem"}
                data-testid={`log-card-rating-${log.id}`}
                starTestIdPrefix={`log-card-star-${log.id}`}
              />
            </div>
          )}

          {/* Copy Button */}
          <div
            onClick={handleCopy}
            title={
              copied ? t("storyproducer.copied", "Copiado!") : t("storyproducer.copy", "Copiar")
            }
            style={{
              background: copied ? "rgba(16, 185, 129, 0.15)" : "transparent",
              color: copied ? "var(--color-accent-success, #10b981)" : "rgba(255,255,255,0.4)",
              width: "22px",
              height: "22px",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
            onMouseEnter={(e) => {
              if (copied) return;
              e.currentTarget.style.color = "#fff";
              e.currentTarget.style.background = "rgba(255,255,255,0.1)";
            }}
            onMouseLeave={(e) => {
              if (copied) return;
              e.currentTarget.style.color = "rgba(255,255,255,0.4)";
              e.currentTarget.style.background = "transparent";
            }}
          >
            <i className={`bx ${copied ? "bx-check" : "bx-copy"}`} style={{ fontSize: "1rem" }}></i>
          </div>

          {/* Edit Button */}
          {onEdit && (
            <div
              onClick={(e) => {
                e.stopPropagation();
                onEdit(log);
              }}
              title="Editar"
              style={{
                background: "transparent",
                color: "rgba(255,255,255,0.4)",
                width: "22px",
                height: "22px",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "#fff";
                e.currentTarget.style.background = "rgba(255,255,255,0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "rgba(255,255,255,0.4)";
                e.currentTarget.style.background = "transparent";
              }}
            >
              <i className="bx bx-pencil" style={{ fontSize: "1rem" }}></i>
            </div>
          )}

          {/* Delete Button */}
          {(onDelete || blockRowCtx?.onDeleteBlock) && (
            <div
              onClick={(e) => {
                e.stopPropagation();
                if (onDelete) {
                  onDelete(log.id);
                } else if (blockRowCtx?.onDeleteBlock) {
                  blockRowCtx.onDeleteBlock();
                }
              }}
              title="Eliminar"
              style={{
                background: "transparent",
                color: "rgba(255,255,255,0.4)",
                width: "22px",
                height: "22px",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "var(--color-accent-danger)";
                e.currentTarget.style.background = "rgba(247,45,96,0.12)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "rgba(255,255,255,0.4)";
                e.currentTarget.style.background = "transparent";
              }}
            >
              <i className="bx bx-trash" style={{ fontSize: "1rem" }}></i>
            </div>
          )}

          {/* Go to Source Button */}
          {isAssembly && finalNavigate && (
            <div
              onClick={(e) => {
                e.stopPropagation();
                const targetStoryId = log.storyId || log.sourceStoryId || storyId;
                const targetItemId = log.sourceId || log.id;
                const targetType = log.type || "log";
                if (targetStoryId && targetItemId) {
                  finalNavigate(targetStoryId, targetItemId, targetType);
                } else {
                  console.warn("[LogCard] Missing targetStoryId or targetItemId for navigation", {
                    targetStoryId,
                    targetItemId,
                  });
                }
              }}
              title="Mostrar en biblioteca"
              style={{
                background: "transparent",
                color: "rgba(255,255,255,0.4)",
                width: "22px",
                height: "22px",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "#fff";
                e.currentTarget.style.background = "rgba(255,255,255,0.08)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "rgba(255,255,255,0.4)";
                e.currentTarget.style.background = "transparent";
              }}
            >
              <i className="bx bx-search-alt" style={{ fontSize: "1rem" }}></i>
            </div>
          )}

          {/* Metadata Toggle Button */}
          <div
            onClick={(e) => {
              e.stopPropagation();
              setIsMetadataExpanded(!isMetadataExpanded);
            }}
            title={isMetadataExpanded ? "Ocultar Metadatos" : "Ver Metadatos"}
            style={{
              background: isMetadataExpanded ? "rgba(247, 45, 96, 0.15)" : "transparent",
              color: isMetadataExpanded ? "#F72D60" : "#888",
              width: "22px",
              height: "22px",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = "#fff";
              e.currentTarget.style.background = isMetadataExpanded
                ? "rgba(247, 45, 96, 0.4)"
                : "rgba(255,255,255,0.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = isMetadataExpanded ? "#F72D60" : "#888";
              e.currentTarget.style.background = isMetadataExpanded
                ? "rgba(247, 45, 96, 0.15)"
                : "transparent";
            }}
          >
            <i
              className={`bx ${isMetadataExpanded ? "bx-chevron-up" : "bx-chevron-down"}`}
              style={{ fontSize: "1.2rem" }}
            ></i>
          </div>
        </div>

        <div
          contentEditable={blockEditorText !== undefined ? false : undefined}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: compact ? "4px" : "8px",
            paddingRight: (() => {
              const base =
                hasDelete && onNavigateSource ? 55 : hasDelete || onNavigateSource ? 30 : 0;
              // Inline stars sit in the same absolute action bar, so reserve
              // their width too or the header text runs under them.
              return `${base + (showInlineRating && onUpdateItem ? 34 : 0)}px`;
            })(),
          }}
        >
          {displayDrag && (
            <div
              ref={blockRowCtx?.handleRef as any}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                marginRight: "6px",
                cursor: "grab",
              }}
            >
              <i className="bx bx-grid-vertical" style={{ fontSize: "1.1rem", color: "#555" }}></i>
              {index !== undefined && (
                <span style={{ color: "#666", fontSize: "0.75rem", fontWeight: 700 }}>
                  {index + 1}
                </span>
              )}
            </div>
          )}
          <span
            onClick={
              onTimecodeClick
                ? (e) => {
                    e.stopPropagation();
                    onTimecodeClick(log);
                  }
                : undefined
            }
            title={
              onTimecodeClick
                ? t("storyproducer.relayClip.playTooltip", "Ver video en este timecode")
                : undefined
            }
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: compact ? "0.78rem" : "0.85rem",
              color: tcColor,
              fontWeight: 600,
              cursor: onTimecodeClick ? "pointer" : undefined,
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            {dateLabel && <span style={{ opacity: 0.65, marginRight: "6px" }}>{dateLabel}</span>}
            {log.tc}
            {onTimecodeClick && <i className="bx bx-play-circle" style={{ fontSize: "0.9rem" }} />}
          </span>
          {!isAssembly && log.author && (
            <span
              style={{
                fontSize: "0.7rem",
                color: "var(--color-data-log)",
                fontWeight: 700,
              }}
            >
              {cleanAuthorName(log.author)}
            </span>
          )}
        </div>
        {/* Text with @mention badges and CAM badges (original case preserved) */}
        {blockEditorText !== undefined ? (
          <div
            className="allow-select"
            data-log-editing="true"
            onClick={(e) => e.stopPropagation()}
            style={{
              color: textColor,
              fontSize: compact ? "0.82rem" : "0.9rem",
              lineHeight: compact ? 1.45 : 1.8,
              marginBottom: compact ? "2px" : "8px",
              fontStyle: textStyle as any,
              cursor: "text",
              outline: "none",
              userSelect: "text",
              WebkitUserSelect: "text",
              whiteSpace: "pre-wrap",
            }}
          >
            {blockEditorText}
          </div>
        ) : isEditingText ? (
          <div data-log-editing="true" style={{ marginBottom: "8px" }}>
            <textarea
              value={draftText}
              autoFocus
              onChange={(e) => setDraftText(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Escape") {
                  setIsEditingText(false);
                } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  onSaveText?.(draftText.trim());
                  setIsEditingText(false);
                }
              }}
              rows={Math.min(8, Math.max(2, draftText.split("\n").length))}
              style={{
                width: "100%",
                background: "rgba(0,0,0,0.35)",
                color: "#eee",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: "6px",
                padding: "8px 10px",
                fontSize: "0.9rem",
                lineHeight: 1.6,
                fontFamily: "inherit",
                resize: "vertical",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
            <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onSaveText?.(draftText.trim());
                  setIsEditingText(false);
                }}
                style={{
                  background: "var(--color-role-sp, #22c55e)",
                  color: "#000",
                  border: "none",
                  borderRadius: "5px",
                  padding: "5px 12px",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Guardar
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditingText(false);
                }}
                style={{
                  background: "transparent",
                  color: "#aaa",
                  border: "1px solid rgba(255,255,255,0.15)",
                  borderRadius: "5px",
                  padding: "5px 12px",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        ) : (
          <div
            className={draggable ? undefined : "allow-select"}
            // Inline edit on click: when the card supports text editing (onSaveText),
            // a click anywhere on the text opens the in-place editor — replacing the
            // former pencil button. stopPropagation keeps card/clip clicks from firing.
            onClick={
              onSaveText
                ? (e) => {
                    e.stopPropagation();
                    setDraftText(textContent || "");
                    setIsEditingText(true);
                  }
                : undefined
            }
            title={onSaveText ? "Editar" : undefined}
            style={{
              color: textColor,
              fontSize: compact ? "0.82rem" : "0.9rem",
              lineHeight: compact ? 1.45 : 1.8,
              marginBottom: compact ? "2px" : "8px",
              fontStyle: textStyle as any,
              userSelect: draggable ? "none" : undefined,
              WebkitUserSelect: draggable ? "none" : undefined,
              cursor: draggable ? "grab" : onSaveText ? "text" : undefined,
            }}
          >
            {textContent && formatLogText(textContent, participants)}
          </div>
        )}

        {/* Universal Metadata Panel */}
        {isMetadataExpanded && (
          <div
            contentEditable={blockEditorText !== undefined ? false : undefined}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              columnGap: "16px",
              rowGap: "8px",
              marginTop: "8px",
              padding: "12px",
              background: "rgba(0,0,0,0.2)",
              borderRadius: "6px",
              border: "1px solid rgba(255,255,255,0.05)",
            }}
          >
            {/* ── ROW 1: Context Pills ── */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "6px",
                marginBottom: "16px",
                gridColumn: "1 / -1",
              }}
            >
              {log.rundownName && (
                <span
                  style={{
                    fontSize: "0.65rem",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    background: "rgba(0,0,0,0.4)",
                    color: "#F59E0B",
                    border: "1px solid rgba(245,158,11,0.2)",
                  }}
                >
                  <span style={{ opacity: 0.6, marginRight: "4px" }}>Rundown</span>{" "}
                  <span style={{ fontWeight: 600 }}>{log.rundownName}</span>
                </span>
              )}
              {log.eventName && (
                <span
                  style={{
                    fontSize: "0.65rem",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    background: "rgba(0,0,0,0.4)",
                    color: "#0EA5E9",
                    border: "1px solid rgba(14,165,233,0.2)",
                  }}
                >
                  <span style={{ opacity: 0.6, marginRight: "4px" }}>Evento</span>{" "}
                  <span style={{ fontWeight: 600 }}>{log.eventName}</span>
                </span>
              )}
              {log.storyName && (
                <span
                  style={{
                    fontSize: "0.65rem",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    background: "rgba(0,0,0,0.4)",
                    color: "#10B981",
                    border: "1px solid rgba(16,185,129,0.2)",
                  }}
                >
                  <span style={{ opacity: 0.6, marginRight: "4px" }}>Historia</span>{" "}
                  <span style={{ fontWeight: 600 }}>{log.storyName}</span>
                </span>
              )}
              {log.author && (
                <span
                  style={{
                    fontSize: "0.65rem",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    background: "rgba(0,0,0,0.4)",
                    color: "#F72D60",
                    border: "1px solid rgba(247,45,96,0.2)",
                  }}
                >
                  <span style={{ opacity: 0.6, marginRight: "4px" }}>Logger</span>{" "}
                  <span style={{ fontWeight: 600 }}>{cleanAuthorName(log.author)}</span>
                </span>
              )}
            </div>

            {/* ── LEFT COLUMN: Entry Metadata ── */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                paddingRight: "16px",
                borderRight: "1px solid rgba(255,255,255,0.06)",
              }}
            >
              <div
                style={{
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  color: "#F72D60",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Metadatos de Entrada
              </div>

              {/* Tipo de Entrada */}
              <div style={{ display: "flex", alignItems: "center" }}>
                <span
                  style={{ width: "120px", fontSize: "0.7rem", color: "#888", fontWeight: 600 }}
                >
                  Tipo:
                </span>
                <span
                  style={{
                    fontSize: "0.75rem",
                    color:
                      !isOtf && !isTranscription && !isPleca && !isRecurso ? "#F72D60" : tcColor,
                    fontWeight: "bold",
                  }}
                >
                  {isOtf
                    ? "OTF"
                    : isTranscription
                      ? "Transcripción"
                      : isPleca
                        ? "Pleca"
                        : isRecurso
                          ? "Recurso"
                          : "Log"}
                </span>
              </div>

              {/* IN */}
              <div style={{ display: "flex", alignItems: "center" }}>
                <span
                  style={{ width: "120px", fontSize: "0.7rem", color: "#888", fontWeight: 600 }}
                >
                  IN:
                </span>
                <span
                  style={{ fontSize: "0.75rem", color: "#ccc", fontFamily: "var(--font-mono)" }}
                >
                  {log.tc
                    ? (() => {
                        // Si log.tc ya tiene 4 partes (HH:MM:SS;FF o HH:MM:SS:FF), mostrarlo tal cual
                        const tcParts = log.tc.split(/[:;]/);
                        if (tcParts.length >= 4) return log.tc;
                        // Derivar frames del startAt/createdAt si están disponibles (retroactivo)
                        const src = log.startAt || log.start_at || log.createdAt || log.created_at;
                        if (src) {
                          const d = new Date(typeof src === "number" ? src : src);
                          const ms = d.getMilliseconds();
                          if (ms > 0) {
                            const frames = Math.floor((ms / 1000) * 29.97);
                            return `${log.tc};${String(frames).padStart(2, "0")}`;
                          }
                        }
                        return `${log.tc};00`;
                      })()
                    : "-"}
                </span>
              </div>

              {/* OUT */}
              <div style={{ display: "flex", alignItems: "center" }}>
                <span
                  style={{ width: "120px", fontSize: "0.7rem", color: "#888", fontWeight: 600 }}
                >
                  OUT:
                </span>
                <span
                  style={{ fontSize: "0.75rem", color: "#ccc", fontFamily: "var(--font-mono)" }}
                >
                  {derivedOutTc ? (
                    derivedOutTc
                  ) : normalizedEndAt ? (
                    (() => {
                      const ea = normalizedEndAt;
                      if (typeof ea === "string" && ea.includes("T")) {
                        try {
                          const d = new Date(ea.includes("Z") || ea.includes("+") ? ea : ea + "Z");
                          return formatTimecode(d);
                        } catch {
                          return String(ea);
                        }
                      }
                      return String(ea);
                    })()
                  ) : (
                    <span style={{ color: "#555", fontStyle: "italic", fontFamily: "inherit" }}>
                      Sin definir
                    </span>
                  )}
                </span>
              </div>

              {/* DUR */}
              <div style={{ display: "flex", alignItems: "center" }}>
                <span
                  style={{ width: "120px", fontSize: "0.7rem", color: "#888", fontWeight: 600 }}
                >
                  DURACIÓN:
                </span>
                <span
                  style={{ fontSize: "0.75rem", color: "#ccc", fontFamily: "var(--font-mono)" }}
                >
                  {derivedDuration || "-"}
                </span>
              </div>

              {/* Tags de Entrada */}
              <div style={{ display: "flex", alignItems: "flex-start" }}>
                <span
                  style={{
                    width: "120px",
                    fontSize: "0.7rem",
                    color: "#888",
                    fontWeight: 600,
                    marginTop: "2px",
                    flexShrink: 0,
                  }}
                >
                  Tags:
                </span>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                  {log.tags?.length > 0 || log.participants?.length > 0 ? (
                    <>
                      {log.participants?.map((pId: string) => {
                        const p = participants.find(
                          (pp: any) => pp.id?.toLowerCase() === pId.toLowerCase(),
                        );
                        const pName = p?.alias || p?.name || pId;
                        const colorObj = getParticipantColor(
                          p?.metadata?.colorId || p?.colorId,
                          p?.metadata?.customColor || p?.customColor,
                        );
                        return (
                          <span
                            key={`in-p-${pId}`}
                            style={{
                              fontSize: "0.65rem",
                              padding: "2px 8px",
                              borderRadius: "12px",
                              background: colorObj.bg,
                              color: isColorLight(colorObj.bg) ? "#000" : "#fff",
                              fontWeight: 600,
                              textTransform: "uppercase",
                            }}
                          >
                            {pName}
                          </span>
                        );
                      })}
                      {(() => {
                        const displayTags =
                          log.entryTags?.length > 0
                            ? log.entryTags
                            : (log.tags || []).filter(
                                (t: string) =>
                                  !(log.storyTags || []).includes(t) &&
                                  !(log.streams || []).includes(t),
                              );
                        return displayTags.map((tagId: string) => {
                          const cameraName = getCameraName(tagId);
                          if (cameraName) {
                            return (
                              <span
                                key={tagId}
                                style={{
                                  fontSize: "0.65rem",
                                  padding: "2px 8px",
                                  borderRadius: "12px",
                                  background: "#FACC15",
                                  color: isColorLight("#FACC15") ? "#000" : "#fff",
                                  fontWeight: 900,
                                  textTransform: "uppercase",
                                }}
                              >
                                {cameraName}
                              </span>
                            );
                          }
                          const label = getTagLabel(tagId);
                          if (label === tagId && /^[0-9A-F]{8}-[0-9A-F]{4}-/i.test(tagId))
                            return null;
                          const color = getTagColor(tagId);
                          return (
                            <span
                              key={tagId}
                              style={{
                                fontSize: "0.65rem",
                                padding: "2px 8px",
                                borderRadius: "12px",
                                background: color,
                                color: isColorLight(color) ? "#000" : "#fff",
                                fontWeight: 600,
                                textTransform: "uppercase",
                              }}
                            >
                              {label}
                            </span>
                          );
                        });
                      })()}
                    </>
                  ) : (
                    <span style={{ fontSize: "0.7rem", color: "#555", fontStyle: "italic" }}>
                      Sin tags
                    </span>
                  )}
                </div>
              </div>

              {/* Ranking Entrada */}
              <div style={{ display: "flex", alignItems: "center" }}>
                <span
                  style={{ width: "120px", fontSize: "0.7rem", color: "#888", fontWeight: 600 }}
                >
                  Ranking:
                </span>
                <div style={{ display: "flex", gap: "2px", alignItems: "center" }}>
                  <StarRating
                    value={log.rating || 0}
                    onChange={
                      onUpdateItem
                        ? (v) => {
                            onUpdateItem(log.id, { rating: v > 0 ? v : undefined });
                          }
                        : undefined
                    }
                    inactiveColor="rgba(255,255,255,0.15)"
                    size="var(--text-lg)"
                  />
                  {(!log.rating || log.rating === 0) && !onUpdateItem && (
                    <span style={{ fontSize: "0.7rem", color: "#555", fontStyle: "italic" }}>
                      Sin rankear
                    </span>
                  )}
                </div>
              </div>

              {/* Fecha */}
              <div style={{ display: "flex", alignItems: "center" }}>
                <span
                  style={{ width: "120px", fontSize: "0.7rem", color: "#888", fontWeight: 600 }}
                >
                  Fecha:
                </span>
                <span
                  style={{ fontSize: "0.75rem", color: "#ccc", fontFamily: "var(--font-mono)" }}
                >
                  {dateLabel
                    ? (() => {
                        const parts = dateLabel.split("-");
                        return parts.length === 3
                          ? `${parts[2]}-${parts[1]}-${parts[0]}`
                          : dateLabel;
                      })()
                    : "-"}
                </span>
              </div>

              {/* ID */}
              <div style={{ display: "flex", alignItems: "center" }}>
                <span
                  style={{ width: "120px", fontSize: "0.7rem", color: "#888", fontWeight: 600 }}
                >
                  ID original:
                </span>
                <span
                  style={{
                    fontSize: "0.65rem",
                    color: "#555",
                    fontFamily: "var(--font-mono)",
                    wordBreak: "break-all",
                  }}
                >
                  {log.sourceId || log.id || "-"}
                </span>
              </div>
              {log.sourceId && log.id && log.sourceId !== log.id && (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span
                    style={{ width: "120px", fontSize: "0.7rem", color: "#888", fontWeight: 600 }}
                  >
                    ID corte:
                  </span>
                  <span
                    style={{
                      fontSize: "0.65rem",
                      color: "#555",
                      fontFamily: "var(--font-mono)",
                      wordBreak: "break-all",
                    }}
                  >
                    {log.id}
                  </span>
                </div>
              )}
            </div>

            {/* ── RIGHT COLUMN: Story Metadata ── */}
            <div
              style={{ display: "flex", flexDirection: "column", gap: "10px", paddingLeft: "16px" }}
            >
              <div
                style={{
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  color: "#10B981",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Metadatos de Historia
              </div>

              {/* Stream */}
              <div style={{ display: "flex", alignItems: "flex-start" }}>
                <span
                  style={{
                    width: "120px",
                    fontSize: "0.7rem",
                    color: "#888",
                    fontWeight: 600,
                    marginTop: "2px",
                    flexShrink: 0,
                  }}
                >
                  Stream:
                </span>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                  {(log.streams?.length ? log.streams : log.cameras)?.length > 0 ? (
                    (log.streams?.length ? log.streams : log.cameras).map((streamId: string) => {
                      const label = getCameraName(streamId) || streamId;
                      return (
                        <span
                          key={streamId}
                          style={{
                            fontSize: "0.65rem",
                            padding: "2px 8px",
                            borderRadius: "12px",
                            background: "#ff9933",
                            color: "#000",
                            fontWeight: 600,
                            textTransform: "uppercase",
                          }}
                        >
                          {label}
                        </span>
                      );
                    })
                  ) : (
                    <span style={{ fontSize: "0.7rem", color: "#555", fontStyle: "italic" }}>
                      Sin asignar
                    </span>
                  )}
                </div>
              </div>

              {/* Tags de Historia */}
              <div style={{ display: "flex", alignItems: "flex-start" }}>
                <span
                  style={{
                    width: "120px",
                    fontSize: "0.7rem",
                    color: "#888",
                    fontWeight: 600,
                    marginTop: "2px",
                    flexShrink: 0,
                  }}
                >
                  Tags:
                </span>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                  {log.storyTags?.length > 0 || log.storyParticipants?.length > 0 ? (
                    <>
                      {log.storyParticipants?.map((pId: string) => {
                        const p = participants.find(
                          (pp: any) => pp.id?.toLowerCase() === pId.toLowerCase(),
                        );
                        const pName = p?.alias || p?.name || pId;
                        const colorObj = getParticipantColor(
                          p?.metadata?.colorId || p?.colorId,
                          p?.metadata?.customColor || p?.customColor,
                        );
                        return (
                          <span
                            key={`story-p-${pId}`}
                            style={{
                              fontSize: "0.65rem",
                              padding: "2px 8px",
                              borderRadius: "12px",
                              background: colorObj.bg,
                              color: isColorLight(colorObj.bg) ? "#000" : "#fff",
                              fontWeight: 600,
                              textTransform: "uppercase",
                            }}
                          >
                            {pName}
                          </span>
                        );
                      })}
                      {log.storyTags?.map((tagId: string) => {
                        const cameraName = getCameraName(tagId);
                        if (cameraName) {
                          return (
                            <span
                              key={tagId}
                              style={{
                                fontSize: "0.65rem",
                                padding: "2px 8px",
                                borderRadius: "12px",
                                background: "#FACC15",
                                color: isColorLight("#FACC15") ? "#000" : "#fff",
                                fontWeight: 900,
                                textTransform: "uppercase",
                              }}
                            >
                              {cameraName}
                            </span>
                          );
                        }
                        const label = getTagLabel(tagId);
                        if (label === tagId && /^[0-9A-F]{8}-[0-9A-F]{4}-/i.test(tagId))
                          return null;
                        const color = getTagColor(tagId);
                        return (
                          <span
                            key={tagId}
                            style={{
                              fontSize: "0.65rem",
                              padding: "2px 8px",
                              borderRadius: "12px",
                              background: color,
                              color: isColorLight(color) ? "#000" : "#fff",
                              fontWeight: 600,
                              textTransform: "uppercase",
                            }}
                          >
                            {label}
                          </span>
                        );
                      })}
                    </>
                  ) : (
                    <span style={{ fontSize: "0.7rem", color: "#555", fontStyle: "italic" }}>
                      Sin tags
                    </span>
                  )}
                </div>
              </div>

              {/* Ranking Historia */}
              <div style={{ display: "flex", alignItems: "center" }}>
                <span
                  style={{ width: "120px", fontSize: "0.7rem", color: "#888", fontWeight: 600 }}
                >
                  Ranking:
                </span>
                <div style={{ display: "flex", gap: "2px" }}>
                  {log.storyRating && log.storyRating > 0 ? (
                    Array.from({ length: log.storyRating }).map((_, i) => (
                      <i
                        key={i}
                        className="bx bxs-star"
                        style={{ color: "#10B981", fontSize: "0.9rem" }}
                      ></i>
                    ))
                  ) : (
                    <span style={{ fontSize: "0.7rem", color: "#555", fontStyle: "italic" }}>
                      Sin rankear
                    </span>
                  )}
                </div>
              </div>

              {/* Director */}
              <div style={{ display: "flex", alignItems: "center" }}>
                <span
                  style={{ width: "120px", fontSize: "0.7rem", color: "#888", fontWeight: 600 }}
                >
                  Director:
                </span>
                <span style={{ fontSize: "0.75rem", color: "#ccc" }}>
                  {log.storyDirectorId || log.directorId ? (
                    directors?.find((d: any) => d.id === (log.storyDirectorId || log.directorId))
                      ?.name ||
                    participants.find((p: any) => p.id === (log.storyDirectorId || log.directorId))
                      ?.name ||
                    log.storyDirectorId ||
                    log.directorId
                  ) : (
                    <span style={{ color: "#555", fontStyle: "italic" }}>Sin asignar</span>
                  )}
                </span>
              </div>
            </div>

            {/* Word Timestamps Dropdown */}
            {log.words && log.words.length > 0 && (
              <div
                style={{
                  marginTop: "16px",
                  borderTop: "1px solid rgba(255,255,255,0.05)",
                  paddingTop: "12px",
                }}
              >
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsWordsExpanded(!isWordsExpanded);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    cursor: "pointer",
                    padding: "6px 8px",
                    background: isWordsExpanded ? "rgba(247, 45, 96, 0.15)" : "rgba(0,0,0,0.2)",
                    color: isWordsExpanded ? "#F72D60" : "#888",
                    borderRadius: "4px",
                    width: "fit-content",
                    border: "1px solid rgba(255,255,255,0.05)",
                    transition: "all 0.2s ease",
                  }}
                >
                  <i
                    className={`bx bx-chevron-${isWordsExpanded ? "up" : "down"}`}
                    style={{ fontSize: "1.1rem" }}
                  ></i>
                  <span style={{ fontSize: "0.75rem", fontWeight: 600 }}>
                    TIMESTAMPS DE PALABRAS ({log.words.length})
                  </span>
                </div>

                {isWordsExpanded && (
                  <div
                    style={{
                      marginTop: "12px",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "6px",
                      background: "rgba(0,0,0,0.3)",
                      padding: "12px",
                      borderRadius: "6px",
                      borderLeft: `2px solid ${accentColor}`,
                    }}
                  >
                    {log.words.map((wordObj: any, i: number) => {
                      const startTime =
                        typeof wordObj.start === "number" ? wordObj.start.toFixed(2) : "?";
                      const endTime =
                        typeof wordObj.end === "number" ? wordObj.end.toFixed(2) : "?";
                      return (
                        <div
                          key={i}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            background: "rgba(255,255,255,0.05)",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            border: "1px solid rgba(255,255,255,0.1)",
                          }}
                        >
                          <span style={{ color: "#fff", fontSize: "0.8rem", fontWeight: 600 }}>
                            {wordObj.word || wordObj.text}
                          </span>
                          <span
                            style={{ color: "#888", fontSize: "0.65rem", fontFamily: "monospace" }}
                          >
                            [{startTime}s - {endTime}s]
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {/* Inline Transcriptions */}
        {transcriptCount > 0 && (
          <div
            contentEditable={blockEditorText !== undefined ? false : undefined}
            style={{ marginTop: "8px" }}
          >
            <div
              onClick={(e) => {
                e.stopPropagation();
                if (toggleTranscriptExpand) toggleTranscriptExpand(log.id);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                cursor: "pointer",
                padding: "6px 8px",
                background: "rgba(0,0,0,0.2)",
                borderRadius: "4px",
                width: "fit-content",
                border: "1px solid rgba(255,255,255,0.05)",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(0,0,0,0.4)";
                e.currentTarget.style.border = `1px solid ${accentColor}`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(0,0,0,0.2)";
                e.currentTarget.style.border = "1px solid rgba(255,255,255,0.05)";
              }}
            >
              <i
                className={`bx bx-chevron-${isTranscriptExpanded ? "up" : "down"}`}
                style={{ color: accentColor, fontSize: "1.1rem" }}
              ></i>
              <span style={{ fontSize: "0.75rem", color: "#aaa", fontWeight: 600 }}>
                {transcriptCount} {transcriptCount === 1 ? "Segmento" : "Segmentos"} (OTF)
              </span>
            </div>

            {isTranscriptExpanded && (
              <div
                style={{
                  marginTop: "8px",
                  padding: "12px",
                  background: "rgba(0,0,0,0.3)",
                  borderRadius: "6px",
                  borderLeft: `2px solid ${accentColor}`,
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                {transcripts?.map((seg: any) => (
                  <div
                    key={seg.id}
                    style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}
                  >
                    <span
                      style={{
                        fontSize: "0.65rem",
                        color: accentColor,
                        fontFamily: "var(--font-mono)",
                        marginTop: "2px",
                      }}
                    >
                      {seg.start ? Math.floor(seg.start) + "s" : ""}
                    </span>
                    <div style={{ flex: 1 }}>
                      <span
                        style={{
                          fontSize: "0.7rem",
                          color: "#fff",
                          fontWeight: 700,
                          marginRight: "6px",
                        }}
                      >
                        {seg.speaker}
                      </span>
                      <span style={{ fontSize: "0.8rem", color: "#ccc", lineHeight: 1.5 }}>
                        {_renderTextWithHighlight(seg.text, highlightText)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
